import { AsyncPipe, CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@core/services/config.service';
import { ThemeService } from '@core/services/theme.service';
import {
  PAGE_TITLES,
  usePageTitleWithLangChange,
} from '@core/utils/page-title.util';
import { PagePreviewBaseComponent } from '@feature/preview-management/components/page-preview-base/page-preview-base.component';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { ErrorState } from '@shared/state/error.state';
import { catchError, map, Observable } from 'rxjs';
import { ChooseLanguageComponent } from '../../../../core/components/choose-language/choose-language.component';
import { NavigationComponent } from '../../../../core/components/navigation/navigation.component';
import { CustomTranslateLoader } from '../../../../core/i18n/custom-translate-loader';
import { AnswerModuleService } from '../../../../core/services/answer-module.service';
import { RedirectService } from '../../../../core/services/redirect.service';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { Icon } from '../../../../shared/components/icon/icon.enum';
import { LayoutComponent } from '../../../../shared/components/layout/layout.component';
import { LanguageSlotDirective } from '../../../../shared/directives/language-slot.directive';
import { AnswerModuleClientRequest } from '../../../../shared/models/answer-module-client-request';
import { AnswerModuleResponse } from '../../../../shared/models/answer-module-response';
import { ChooseLanguage } from '../../../../shared/models/choose-language';
import { EventChooseLanguageComponent } from '../../../../shared/models/components/event-choose-language';
import { PasswordView } from '../../../../shared/models/views-models/password-view';
import {
  mapLanguageOptions,
  updateTextDirection,
} from '../../../../shared/utils/helper.util';
import {
  cleanupViewportHeight,
  setupViewportHeight,
} from '../../../../shared/utils/viewport.util';

@Component({
  selector: 'app-password-page',
  imports: [
    CommonModule,
    NavigationComponent,
    LayoutComponent,
    LanguageSlotDirective,
    FormsModule,
    TranslateModule,
    ChooseLanguageComponent,
    ReactiveFormsModule,
    IconComponent,
    AsyncPipe,
    ValidatorComponent,
  ],
  templateUrl: './password-page.component.html',
  styleUrl: './password-page.component.scss',
})
export class PasswordPageComponent
  extends PagePreviewBaseComponent
  implements OnInit, OnDestroy
{
  private setPageTitle = usePageTitleWithLangChange();
  constructor(
    private route: ActivatedRoute,
    protected override redirectService: RedirectService,
    private answerService: AnswerModuleService,
    private translateService: TranslateService,
    private translateLoader: CustomTranslateLoader,
    private fb: FormBuilder,
    private configService: ConfigService,
    protected override themeService: ThemeService,
  ) {
    super(redirectService, themeService);
    this.useClientValidation = this.configService.get('USE_CLIENT_VALIDATION');
  }

  @HostListener('window:popstate')
  onPopState() {
    history.pushState(null, '');
  }
  private readonly destroyRef = inject(DestroyRef);
  private beforeUnloadHandler = this.onBeforeUnload.bind(this);
  store = inject(Store);
  useClientValidation: boolean;
  passwordView?: PasswordView;
  chooseLanguageComponent?: EventChooseLanguageComponent;
  passwordPageTranslationkeys: string[] = [
    'navigation.',
    'validation.password.incorrect',
    'password.',
  ];
  languageOptions: { value: string; label: string; src: string }[] = [];
  selectedLanguage: string = '';
  passwordForm: FormGroup = new FormGroup({});
  control?: FormControl;
  disableNextButton = false;
  Icon = Icon;
  validator = ValidatorType;
  error$ = this.store
    .select(ErrorState.getErrorsByErrorType)
    .pipe(map((selector) => selector('PasswordIncorrect')));

  onBeforeUnload(event: Event): void {
    event.preventDefault();
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.allowNavigation;
  }

  getIframeQueryParams(): {
    mode: string | null;
    showConditions: boolean;
    evalRules: boolean;
    language: string | null;
  } {
    const search =
      window.location.search ||
      (window.location.hash.includes('?')
        ? '?' + window.location.hash.split('?')[1]
        : '');

    const params = new URLSearchParams(search);
    // const queryObject: { [key: string]: string } = {};
    // params.forEach((value, key) => {
    //   queryObject[key] = value;
    // });
    // console.log(queryObject);

    return {
      mode: params.get('mode'),
      showConditions: params.get('showConditions') === 'true',
      evalRules: params.get('evalRules') === 'true',
      language: params.get('language'),
    };
  }

  protected override onResponseSet(value: AnswerModuleResponse): void {
    if (value.view.type === 'QUEST_PASSWORD') {
      this.initPasswordComponent(value);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    cleanupViewportHeight();
  }

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.PASSWORD.key, PAGE_TITLES.PASSWORD.fallback);

    // Set viewport height for mobile browsers
    setupViewportHeight();

    if (!this.previewMode) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else {
      this.passwordPageTranslationkeys.push('preview.');
    }
    const { evalRules } = this.getIframeQueryParams();
    this.passwordForm = this.fb.group({
      password: ['', evalRules ? [Validators.required] : []],
    });
    this.control = this.passwordForm.get(['password']) as FormControl;
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const response = history.state.data as AnswerModuleResponse;
        if (!this.previewMode) {
          this.response = response;
        }
        this.initPasswordComponent(response);
      });

    if (this.response.language) {
      updateTextDirection(this.response.language);
    }
  }

  initPasswordComponent(response: AnswerModuleResponse) {
    if (response && response.view.type === 'QUEST_PASSWORD') {
      this.passwordView = response.view;
      this.themeService.loadTheme(response.themeId);

      this.chooseLanguageComponent = this.passwordView.data.components.find(
        (i) => i.$type === '@event-choose-language',
      );
      if (this.chooseLanguageComponent) {
        this.languageOptions = mapLanguageOptions(this.chooseLanguageComponent);
      } else {
        this.selectedLanguage = this.response.language;
        this.translateLoader
          .getTranslation(
            this.selectedLanguage,
            this.passwordPageTranslationkeys,
            this.response.fallbackLanguage,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((translation) => {
            this.translateService.setTranslation(
              this.selectedLanguage,
              translation,
              true,
            );
            this.translateService.use(this.selectedLanguage);
          });
      }
    }
  }

  setLanguage({ language }: ChooseLanguage) {
    this.selectedLanguage = language;
    updateTextDirection(language);
  }

  next() {
    if (this.response?.responseId) {
      const body: AnswerModuleClientRequest[] = [
        {
          $type: 'passwordResponseData',
          password: this.control?.value,
          responseId: this.response?.responseId,
        },
        {
          $type: 'languageResponseData',
          responseId: this.response?.responseId,
          language: this.selectedLanguage,
        },
      ];
      if (this.passwordView) {
        this.answerService
          .postCallback(this.passwordView?.data.callbackUrl, body)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            catchError(() => {
              this.allowNavigation = true;
              return [];
            }),
          )
          .subscribe({
            next: (response) => {
              if (response.body) {
                this.handlePreviewMode(response.body);
              }
            },
          });
      }
    }
  }

  onSubmit() {
    if (this.passwordForm?.valid) {
      if (!this.disableNextButton) {
        this.next();
      }
      this.disableNextButton = true;
      setTimeout(() => {
        this.disableNextButton = false;
      }, 1000);
    } else {
      this.control?.markAsTouched();
    }
  }
}
